"use client";

import Link from "next/link";
import { useId, useState, type MouseEvent, type ReactNode } from "react";
import { Knopf } from "../knopf";
import { ariaSortVon, seitenFolge, type Sortierung } from "./tabellen-logik";
import type { TabellenDichte, TabellenSpalte, TabellenZeile } from "./tabellen-typen";

/*
 * Gemeinsame Teile von Register (A), kompakter Tabelle (H) und Baum (B): Zellen-Klassen je Dichte, Sortierkopf,
 * Namenszelle mit Haupt-Link, Karten-Liste für Mobil (T12), Zustandskarte (T17), Skelett, Blättern (T13).
 * Klassenketten sind feste, vollständige Zeichenketten (nie zusammengesetzt); Optik nur Theme-Tokens.
 */

/** Tabellen-Rahmen (Preline `tables-rounded-tables`): Wrapper `relative` + Querscroll (K4). */
export const TABELLEN_RAHMEN =
  "relative overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";

const KOPF: Record<TabellenDichte, { start: string; end: string }> = {
  standard: {
    start: "px-4 py-3 text-start text-xs font-medium text-muted-foreground-1 uppercase",
    end: "px-4 py-3 text-end text-xs font-medium text-muted-foreground-1 uppercase",
  },
  kompakt: {
    start: "px-4 py-2 text-start text-xs font-medium text-muted-foreground-1 uppercase",
    end: "px-4 py-2 text-end text-xs font-medium text-muted-foreground-1 uppercase",
  },
};

const ZELLE: Record<TabellenDichte, { start: string; end: string }> = {
  standard: {
    start: "px-4 py-3 text-sm text-foreground",
    end: "px-4 py-3 text-sm text-foreground text-end tabular-nums",
  },
  kompakt: {
    start: "px-4 py-1.5 text-sm text-foreground",
    end: "px-4 py-1.5 text-sm text-foreground text-end tabular-nums",
  },
};

const NAMEN_ZELLE: Record<TabellenDichte, string> = {
  standard: "px-4 py-3 text-sm font-medium text-foreground text-start",
  kompakt: "px-4 py-1.5 text-sm font-medium text-foreground text-start",
};

const AKTIONEN_ZELLE: Record<TabellenDichte, string> = {
  standard: "relative z-10 px-4 py-3 text-end",
  kompakt: "relative z-10 px-4 py-1.5 text-end",
};

const HAUPT_LINK =
  "block font-medium text-foreground hover:text-primary focus:outline-hidden focus-visible:underline after:absolute after:inset-0";

/** Spalten mit `keinUmbruch`: feste, vollständige Ketten (Kopf und Zelle, Ausrichtung Ende, nur Dichte „standard“ und „kompakt“). */
const KEIN_UMBRUCH_KOPF: Record<TabellenDichte, { start: string; end: string }> = {
  standard: {
    start: "px-4 py-3 text-start text-xs font-medium text-muted-foreground-1 uppercase whitespace-nowrap",
    end: "px-4 py-3 text-end text-xs font-medium text-muted-foreground-1 uppercase whitespace-nowrap",
  },
  kompakt: {
    start: "px-4 py-2 text-start text-xs font-medium text-muted-foreground-1 uppercase whitespace-nowrap",
    end: "px-4 py-2 text-end text-xs font-medium text-muted-foreground-1 uppercase whitespace-nowrap",
  },
};
const KEIN_UMBRUCH_ZELLE: Record<TabellenDichte, { start: string; end: string }> = {
  standard: {
    start: "px-4 py-3 text-sm text-foreground whitespace-nowrap",
    end: "px-4 py-3 text-sm text-foreground text-end tabular-nums whitespace-nowrap",
  },
  kompakt: {
    start: "px-4 py-1.5 text-sm text-foreground whitespace-nowrap",
    end: "px-4 py-1.5 text-sm text-foreground text-end tabular-nums whitespace-nowrap",
  },
};

function spaltenKopfKlasse(dichte: TabellenDichte, s: TabellenSpalte): string {
  const seite = s.ausrichtung === "end" ? "end" : "start";
  return s.keinUmbruch ? KEIN_UMBRUCH_KOPF[dichte][seite] : KOPF[dichte][seite];
}

function spaltenZellenKlasse(dichte: TabellenDichte, s: TabellenSpalte): string {
  const seite = s.ausrichtung === "end" ? "end" : "start";
  return s.keinUmbruch ? KEIN_UMBRUCH_ZELLE[dichte][seite] : ZELLE[dichte][seite];
}

export function kopfKlasse(dichte: TabellenDichte, ausrichtung: TabellenSpalte["ausrichtung"]): string {
  return KOPF[dichte][ausrichtung === "end" ? "end" : "start"];
}
export function namensKopfKlasse(dichte: TabellenDichte): string {
  return KOPF[dichte].start;
}
export function aktionenKopfKlasse(dichte: TabellenDichte): string {
  return KOPF[dichte].end;
}
export function zellenKlasse(dichte: TabellenDichte, ausrichtung: TabellenSpalte["ausrichtung"]): string {
  return ZELLE[dichte][ausrichtung === "end" ? "end" : "start"];
}

const PFEIL_ZU = (
  <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m7 15 5 5 5-5" />
    <path d="m7 9 5-5 5 5" />
  </svg>
);
const PFEIL_AUF = (
  <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12 7-7 7 7" />
    <path d="M12 19V5" />
  </svg>
);
const PFEIL_AB = (
  <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14" />
    <path d="m19 12-7 7-7-7" />
  </svg>
);

/** Spaltenkopf: sortierbar = Knopf im `th` mit `aria-sort` (T3), sonst nur Text. */
export function KopfZelle({
  spalte,
  dichte,
  sortierung,
  onSortieren,
}: {
  spalte: TabellenSpalte;
  dichte: TabellenDichte;
  sortierung?: Sortierung;
  onSortieren?: (spalteId: string) => void;
}) {
  const klasse = spaltenKopfKlasse(dichte, spalte);
  if (!spalte.sortierbar) {
    return (
      <th scope="col" className={klasse}>
        {spalte.kopf}
      </th>
    );
  }
  const wert = ariaSortVon(spalte.id, sortierung);
  return (
    <th scope="col" className={klasse} aria-sort={wert}>
      <button
        type="button"
        className="inline-flex items-center gap-x-1 uppercase hover:text-foreground focus:outline-hidden focus-visible:underline"
        onClick={() => onSortieren?.(spalte.id)}
      >
        {spalte.kopf}
        {wert === "ascending" ? PFEIL_AUF : wert === "descending" ? PFEIL_AB : PFEIL_ZU}
      </button>
    </th>
  );
}

/** Aufklapp-Auslöser der Unterzeile (T6): `Knopf` (Ghost, nur Symbol, xs) mit `aria-expanded`/`aria-controls`, Lucide `ChevronRight`. */
const CHEVRON_ZU =
  "shrink-0 size-4 transition-transform";
const CHEVRON_AUF =
  "shrink-0 size-4 transition-transform rotate-90";

function AufklappKnopf({ offen, onUmschalten, ziel, label }: { offen: boolean; onUmschalten: () => void; ziel: string; label: string }) {
  return (
    <Knopf
      variante="ghost"
      nurSymbol
      groesse="xs"
      ueberZeilenlink
      starr
      ariaExpanded={offen}
      ariaControls={ziel}
      label={label}
      onClick={onUmschalten}
      symbol={
        <svg className={offen ? CHEVRON_AUF : CHEVRON_ZU} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m9 18 6-6-6-6" />
        </svg>
      }
    />
  );
}

/** Einfacher Klick öffnet die Hülle (`onOeffnen`), Strg/Meta/Umschalt/Mittelklick folgen der Adresse. */
function beiHauptLink(zeile: TabellenZeile) {
  return (e: MouseEvent<HTMLAnchorElement>) => {
    if (!zeile.onOeffnen || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    zeile.onOeffnen();
  };
}

/**
 * Name einer Zeile: der Haupt-Link (Text oder `titelInhalt`); ohne `href` der Name ohne Link (Zeile ohne Ziel). Zeilen mit Link
 * und ohne `titelInhalt` behalten ihr bisheriges Markup unverändert.
 */
function ZeilenName({ zeile }: { zeile: TabellenZeile }) {
  const inhalt = zeile.titelInhalt ?? zeile.titel;
  if (!zeile.href) return <span className="block font-medium text-foreground">{inhalt}</span>;
  return (
    <Link href={zeile.href} className={HAUPT_LINK} onClick={beiHauptLink(zeile)}>
      {inhalt}
    </Link>
  );
}

/** Namenszelle: `th scope="row"` mit genau einem Haupt-Link über die ganze Zeile (T2); Zweitzeile nur in Dichte „standard“. */
export function NamensZelle({
  zeile,
  dichte,
  aufklappen,
}: {
  zeile: TabellenZeile;
  dichte: TabellenDichte;
  /** Nur mit Unterzeile: Zustand und Auslöser (von `DatenZeile`). */
  aufklappen?: { offen: boolean; onUmschalten: () => void; ziel: string };
}) {
  const inhalt = (
    <>
      <ZeilenName zeile={zeile} />
      {dichte === "standard" && zeile.zweitzeile && (
        <span className="block text-xs font-normal text-muted-foreground-1">{zeile.zweitzeile}</span>
      )}
    </>
  );
  return (
    <th scope="row" className={NAMEN_ZELLE[dichte]}>
      {aufklappen ? (
        <div className="flex items-start gap-x-2">
          <AufklappKnopf
            offen={aufklappen.offen}
            onUmschalten={aufklappen.onUmschalten}
            ziel={aufklappen.ziel}
            label={zeile.unterzeileLabel ?? "Details anzeigen"}
          />
          <div className="min-w-0">{inhalt}</div>
        </div>
      ) : (
        inhalt
      )}
    </th>
  );
}

/**
 * Tabellenzeile des Registers/kompakter Tabelle (Hover-Fläche, `relative` für die erweiterte Klickfläche).
 * Mit `unterzeile`: aufklappbare Detailzeile (Archetyp C), Zustand je Zeile im Baustein.
 */
export function DatenZeile({
  zeile,
  spalten,
  dichte,
  mitAktionen,
}: {
  zeile: TabellenZeile;
  spalten: TabellenSpalte[];
  dichte: TabellenDichte;
  mitAktionen: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const id = useId();
  const ziel = `${id}-unterzeile`;
  return (
    <>
      <tr className={zeile.obenAusgerichtet ? "relative align-top hover:bg-muted-hover" : "relative hover:bg-muted-hover"}>
        <NamensZelle
          zeile={zeile}
          dichte={dichte}
          aufklappen={zeile.unterzeile ? { offen, onUmschalten: () => setOffen((v) => !v), ziel } : undefined}
        />
        {spalten.map((s) => (
          <td key={s.id} className={spaltenZellenKlasse(dichte, s)}>
            {zeile.zellen[s.id]}
          </td>
        ))}
        {mitAktionen && <td className={AKTIONEN_ZELLE[dichte]}>{zeile.aktionen}</td>}
      </tr>
      {zeile.unterzeile && (
        <tr id={ziel} hidden={!offen}>
          {offen && (
            <td colSpan={spalten.length + 1 + (mitAktionen ? 1 : 0)} className="px-4 pb-4 pt-1">
              {zeile.unterzeile}
            </td>
          )}
        </tr>
      )}
    </>
  );
}

/** Eine Karte der Mobil-Liste; mit Unterzeile aufklappbar (Zustand je Karte). */
function KartenZeile({ z, spalten }: { z: TabellenZeile; spalten: TabellenSpalte[] }) {
  const [offen, setOffen] = useState(false);
  const id = useId();
  const ziel = `${id}-unterzeile`;
  return (
    <li className="relative px-4 py-3">
      <div className="flex items-start justify-between gap-x-2">
        <div className="min-w-0 flex items-start gap-x-2">
          {z.unterzeile && <AufklappKnopf offen={offen} onUmschalten={() => setOffen((v) => !v)} ziel={ziel} label={z.unterzeileLabel ?? "Details anzeigen"} />}
          <div className="min-w-0">
            <ZeilenName zeile={z} />
            {z.zweitzeile && <span className="block text-xs text-muted-foreground-1">{z.zweitzeile}</span>}
          </div>
        </div>
        {z.aktionen && <div className="relative z-10 shrink-0">{z.aktionen}</div>}
      </div>
      {spalten.length > 0 && (
        <dl className="mt-2 space-y-1">
          {spalten.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-x-3 text-sm">
              <dt className="text-muted-foreground-1">{s.kopf}</dt>
              <dd className="min-w-0 text-foreground text-end">{z.zellen[s.id]}</dd>
            </div>
          ))}
        </dl>
      )}
      {z.unterzeile && (
        <div id={ziel} hidden={!offen} className="mt-3">
          {offen && (z.unterzeileMobil ?? z.unterzeile)}
        </div>
      )}
    </li>
  );
}

/**
 * Mobil (T12): Karten-Liste aus demselben Datensatz; unter 768 px sichtbar (`md:hidden`). Titel = Haupt-Link mit
 * erweiterter Fläche, Zweitzeile, je Spalte „Kopf: Wert“, Aktionen rechts oben (`relative z-10`).
 */
export function KartenListe({ zeilen, spalten }: { zeilen: TabellenZeile[]; spalten: TabellenSpalte[] }) {
  return (
    <ul className="md:hidden divide-y divide-line-2">
      {zeilen.map((z) => (
        <KartenZeile key={z.id} z={z} spalten={spalten} />
      ))}
    </ul>
  );
}

/** Skelett-Zeilen in der Tabellenform (T17 „lädt“): `animate-pulse`, Fläche `bg-surface-1`. */
export function SkelettKoerper({ spaltenAnzahl, dichte, zeilen = 5 }: { spaltenAnzahl: number; dichte: TabellenDichte; zeilen?: number }) {
  return (
    <tbody className="divide-y divide-table-line">
      {Array.from({ length: zeilen }, (_, i) => (
        <tr key={i}>
          {Array.from({ length: spaltenAnzahl }, (_, j) => (
            <td key={j} className={dichte === "kompakt" ? "px-4 py-1.5" : "px-4 py-3"}>
              <div className={j === 0 ? "h-4 w-3/4 rounded bg-surface-1 animate-pulse" : "h-4 w-1/2 rounded bg-surface-1 animate-pulse"} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function SkelettKarten({ zeilen = 3 }: { zeilen?: number }) {
  return (
    <ul className="md:hidden divide-y divide-line-2" aria-hidden="true">
      {Array.from({ length: zeilen }, (_, i) => (
        <li key={i} className="px-4 py-3 space-y-2">
          <div className="h-4 w-2/3 rounded bg-surface-1 animate-pulse" />
          <div className="h-3 w-1/3 rounded bg-surface-1 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

export interface ZustandAktion {
  text: string;
  href?: string;
  onKlick?: () => void;
}

const SYMBOL_KLASSE = "shrink-0 size-4";

function ZustandSymbol({ art }: { art: "leer" | "keineTreffer" | "fehler" }) {
  return (
    <svg className={SYMBOL_KLASSE} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {art === "keineTreffer" && (
        <>
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </>
      )}
      {art === "leer" && (
        <>
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </>
      )}
      {art === "fehler" && (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" x2="12" y1="8" y2="12" />
          <line x1="12" x2="12.01" y1="16" y2="16" />
        </>
      )}
    </svg>
  );
}

/**
 * Zustandskarte statt Tabelle (T17): leer ≠ keine Treffer ≠ Fehler; ein Satz, höchstens eine Aktion.
 * Fehler ist ein wiederholbarer Fehlschlag (r105) ohne Code, `role="alert"`; die anderen `role="status"`.
 */
export function ZustandsKarte({
  art,
  titel,
  text,
  aktion,
}: {
  art: "leer" | "keineTreffer" | "fehler";
  titel: string;
  text?: string;
  aktion?: ZustandAktion;
}) {
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-alerts-recovery/feedback-and-states-cat-empty-states/no-events-empty-state-with-import-and-create-actions */}
      <div className="p-5 flex flex-col justify-center items-center text-center" role={art === "fehler" ? "alert" : "status"}>
        <div className={aktion ? "mb-6 max-w-md mx-auto" : "max-w-md mx-auto"}>
          <div className="inline-flex size-8 items-center justify-center rounded-full bg-surface border border-surface-line text-surface-foreground">
            <ZustandSymbol art={art} />
          </div>
          <p className="mt-3 font-medium text-foreground">{titel}</p>
          {text && <p className="mt-1 text-sm text-muted-foreground-1">{text}</p>}
        </div>
        {aktion &&
          (aktion.href ? (
            <Knopf groesse="sm" href={aktion.href}>
              {aktion.text}
            </Knopf>
          ) : (
            <Knopf groesse="sm" onClick={aktion.onKlick}>
              {aktion.text}
            </Knopf>
          ))}
      </div>
      {/* End Empty State */}
    </>
  );
}

export interface SeitenAngabe {
  aktuell: number;
  gesamt: number;
  /** Seiten als Links (`?page=`); sonst Knöpfe mit `onWechsel`. */
  hrefFuer?: (seite: number) => string;
  onWechsel?: (seite: number) => void;
}

const SEITEN_KNOPF =
  "min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-1.5 text-sm rounded-lg text-foreground hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus disabled:opacity-50 disabled:pointer-events-none";
const SEITEN_KNOPF_AKTIV =
  "min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-1.5 text-sm rounded-lg bg-muted text-foreground focus:outline-hidden";
const SEITEN_KNOPF_AUS =
  "min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-1.5 text-sm rounded-lg text-muted-foreground-1 opacity-50 pointer-events-none";

/**
 * Blättern (T13, Register): `<nav aria-label="Seiten">`, aktuelle Seite `aria-current="page"`, Vor/Zurück.
 * Fuß-Text „Seite n von m“ links. Preline `tables-with-pagination` / Projekt-Tabelle (Fuß).
 */
export function Seitenwahl({ aktuell, gesamt, hrefFuer, onWechsel, anzahlText }: SeitenAngabe & { anzahlText?: string }) {
  if (gesamt < 1) return null;
  const folge = seitenFolge(aktuell, gesamt);
  const zurueck = aktuell > 1 ? aktuell - 1 : null;
  const weiter = aktuell < gesamt ? aktuell + 1 : null;

  const element = (ziel: number, klasse: string, inhalt: ReactNode, label?: string, aktuellSeite = false) =>
    hrefFuer ? (
      <Link key={`${ziel}-${label ?? ""}`} href={hrefFuer(ziel)} className={klasse} aria-label={label} aria-current={aktuellSeite ? "page" : undefined}>
        {inhalt}
      </Link>
    ) : (
      <button key={`${ziel}-${label ?? ""}`} type="button" className={klasse} aria-label={label} aria-current={aktuellSeite ? "page" : undefined} onClick={() => onWechsel?.(ziel)}>
        {inhalt}
      </button>
    );

  const pfeil = (richtung: "zurueck" | "weiter") => (
    <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={richtung === "zurueck" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
    </svg>
  );

  return (
    <>
      {/* Preline: data-display/data-display-sub-tables-records/data-display-cat-project-tables/project-table-with-search-and-filters (Fuß mit Blättern) */}
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-t border-table-line">
        <p className="text-sm text-muted-foreground-1">
          {anzahlText && <span>{anzahlText}</span>}
          {anzahlText && (
            <span className="px-1.5" aria-hidden="true">
              ·
            </span>
          )}
          <span>
            Seite {aktuell} von {gesamt}
          </span>
        </p>
        <nav aria-label="Seiten" className="flex items-center gap-x-1">
          {zurueck !== null ? (
            element(zurueck, SEITEN_KNOPF, <>{pfeil("zurueck")}<span>Zurück</span></>)
          ) : hrefFuer ? (
            <span className={SEITEN_KNOPF_AUS} aria-disabled="true">
              {pfeil("zurueck")}
              <span>Zurück</span>
            </span>
          ) : (
            <button type="button" className={SEITEN_KNOPF_AUS} disabled aria-disabled="true">
              {pfeil("zurueck")}
              <span>Zurück</span>
            </button>
          )}
          <div className="flex items-center gap-x-1">
            {folge.map((s, i) =>
              s === "luecke" ? (
                <span key={`l${i}`} className="px-1 text-sm text-muted-foreground-1" aria-hidden="true">
                  …
                </span>
              ) : (
                element(s, s === aktuell ? SEITEN_KNOPF_AKTIV : SEITEN_KNOPF, s, `Seite ${s}`, s === aktuell)
              ),
            )}
          </div>
          {weiter !== null ? (
            element(weiter, SEITEN_KNOPF, <><span>Weiter</span>{pfeil("weiter")}</>)
          ) : hrefFuer ? (
            <span className={SEITEN_KNOPF_AUS} aria-disabled="true">
              <span>Weiter</span>
              {pfeil("weiter")}
            </span>
          ) : (
            <button type="button" className={SEITEN_KNOPF_AUS} disabled aria-disabled="true">
              <span>Weiter</span>
              {pfeil("weiter")}
            </button>
          )}
        </nav>
      </div>
      {/* End Pagination */}
    </>
  );
}
